import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { fetchTemplates, createTemplate, updateTemplate, deleteTemplate } from "../exports/services/exportJobsService";
import { Toast, useToast } from "../../components/Toast";
import { AvailableColumnsPicker } from "./AvailableColumnsPicker";
import { TextField, TransportFields } from "../exports/components/TransportFields";

const TRANSPORT_OPTIONS = [
  { value: "sftp", label: "SFTP" },
  { value: "http_get", label: "HTTP GET" },
  { value: "http_post", label: "HTTP POST" },
  { value: "cj_graphql", label: "CJ GraphQL" },
];

const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "10px 12px", background: "#FAFAFA",
  border: "1px solid #E2E2E2", borderRadius: 8, color: "#151515", fontSize: 13, marginBottom: 10, outline: "none",
};

const emptyForm = { platform_name: "", transport_type: "sftp", field_mappings: [], base_destination_config: { production: {} } };

// available_columns is derived from a template's column-type field_mappings
// entries — every checked column always gets a default mapping (see
// AvailableColumnsPicker), so there's no separate "available but unmapped"
// set to track independently.
function deriveAvailableColumns(fieldMappings) {
  return fieldMappings
    .filter((m) => m.type === "column")
    .map((m) => ({ source_column: m.source_column, label: m.source_column }));
}

// Support-only template CRUD (real write enforcement is DB-side RLS via
// current_user_is_support() — the isSupportUser gate here is UI-only
// courtesy, not a security boundary).
export function ExportTemplatesAdmin() {
  const { isSupportUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  // Bumped on every resetForm() so AvailableColumnsPicker's `key` changes even
  // between two successive "new template" forms (editingId stays null in both
  // cases) — without this, useFieldMappings' one-time seed would leave the
  // previous template's checked columns lingering in the next blank form.
  const [formResetNonce, setFormResetNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  // Mirrors AvailableColumnsPicker/useFieldMappings validity: false for an
  // empty mapping list, an empty target_key, or a duplicate target_key —
  // the same rules validate_field_mappings() enforces DB-side. Starts false
  // because a new template has no mappings yet.
  const [mappingsValid, setMappingsValid] = useState(false);
  const [toast, showToast] = useToast(5000);

  const load = async () => {
    setLoading(true);
    const { data, error } = await fetchTemplates({ activeOnly: false });
    if (error) showToast(`Failed to load templates: ${error.message}`, "error");
    setTemplates(data || []);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (!isSupportUser) {
    return <div style={{ color: "#7b7b7b", fontSize: 13 }}>Support access required.</div>;
  }

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      platform_name: t.platform_name,
      transport_type: t.transport_type,
      field_mappings: t.base_field_mappings ?? [],
      base_destination_config: { production: { ...(t.base_destination_config?.production || {}) } },
    });
  };

  const resetForm = () => { setEditingId(null); setForm(emptyForm); setFormResetNonce((n) => n + 1); };

  // base_destination_config is a PARTIAL default seeded into new export_jobs
  // (e.g. a partner's global endpoint) — every field here is optional, and
  // credentials never live on a template (those are per-advertiser Vault
  // secrets attached at the job level). See 073's comment on the column.
  const updateBaseDestProduction = (patch) =>
    setForm((f) => ({ ...f, base_destination_config: { production: { ...(f.base_destination_config?.production || {}), ...patch } } }));

  const handleSave = async () => {
    if (!form.platform_name.trim()) { showToast("Platform name is required.", "error"); return; }
    if (!mappingsValid) {
      showToast("Add at least one field mapping, and make every target key non-empty and unique.", "error");
      return;
    }
    setSaving(true);
    const payload = {
      platform_name: form.platform_name.trim(),
      transport_type: form.transport_type,
      available_columns: deriveAvailableColumns(form.field_mappings),
      base_field_mappings: form.field_mappings,
      base_destination_config: { production: { ...(form.base_destination_config?.production || {}) } },
    };
    const { error } = editingId ? await updateTemplate(editingId, payload) : await createTemplate(payload);
    setSaving(false);
    if (error) { showToast(`Failed to save template: ${error.message}`, "error"); return; }
    showToast(editingId ? "Template updated." : "Template created.");
    resetForm();
    load();
  };

  const handleDelete = async (t) => {
    const { error } = await deleteTemplate(t.id);
    if (error) showToast(`Failed to delete template: ${error.message}`, "error");
    else { setTemplates((prev) => prev.filter((x) => x.id !== t.id)); showToast("Template removed."); }
  };

  const handleToggleActive = async (t) => {
    const { error } = await updateTemplate(t.id, { is_active: !t.is_active });
    if (error) showToast(`Failed to update template: ${error.message}`, "error");
    else setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_active: !x.is_active } : x)));
  };

  return (
    <div>
      <h2 style={{ fontSize: 28, fontWeight: 600, color: "#151515", marginBottom: 16, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>Affiliate Platform Templates</h2>

      <div style={{ background: "#FAFAFA", border: "1px solid #E2E2E2", borderRadius: 14, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#151515", marginBottom: 12 }}>{editingId ? "Edit Template" : "New Template"}</div>
        <input type="text" placeholder="Platform name (e.g. Rakuten)" value={form.platform_name} onChange={(e) => setForm((f) => ({ ...f, platform_name: e.target.value }))} style={inputStyle} />
        <select value={form.transport_type} onChange={(e) => setForm((f) => ({ ...f, transport_type: e.target.value, base_destination_config: { production: {} } }))} style={{ ...inputStyle, paddingRight: 32 }}>
          {TRANSPORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <AvailableColumnsPicker
          key={editingId ?? `new-${formResetNonce}`}
          value={form.field_mappings}
          onChange={(fieldMappings) => setForm((f) => ({ ...f, field_mappings: fieldMappings }))}
          onValidityChange={setMappingsValid}
        />

        <div style={{ border: "1px solid #E2E2E2", borderRadius: 12, padding: 16, marginTop: 4, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#151515", marginBottom: 4 }}>Default Destination (optional)</div>
          <div style={{ fontSize: 11, color: "#7b7b7b", marginBottom: 12 }}>
            Pre-fills a new export job's production destination fields — for a partner with one fixed endpoint (e.g. Impact). Every field here is optional and can be overridden per job. Authentication secrets are never entered here; they're attached per-advertiser when a job is configured.
          </div>
          {form.transport_type === "cj_graphql" ? (
            <TextField
              label="Mutation Endpoint"
              value={form.base_destination_config?.production?.mutation_endpoint}
              onChange={(v) => updateBaseDestProduction({ mutation_endpoint: v })}
              placeholder="https://tracking.api.cj.com/graphql"
            />
          ) : (
            <TransportFields
              transportType={form.transport_type}
              values={form.base_destination_config?.production || {}}
              onChange={(values) => setForm((f) => ({ ...f, base_destination_config: { production: values } }))}
              showCredential={false}
            />
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={handleSave} disabled={saving || !form.platform_name.trim() || !mappingsValid} style={{ padding: "9px 16px", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: saving || !mappingsValid ? "default" : "pointer", opacity: !form.platform_name.trim() || !mappingsValid ? 0.5 : 1 }}>
            {saving ? "Saving…" : editingId ? "Save Changes" : "Create Template"}
          </button>
          {editingId && <button onClick={resetForm} style={{ padding: "9px 16px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 12, color: "#626262", cursor: "pointer" }}>Cancel</button>}
        </div>
      </div>

      {loading && <div style={{ color: "#7b7b7b", fontSize: 13 }}>Loading templates…</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {templates.map((t) => (
          <div key={t.id} style={{ background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <span style={{ fontWeight: 600, color: "#151515", fontSize: 13 }}>{t.platform_name}</span>
              <span style={{ marginLeft: 8, fontSize: 12, color: "#626262" }}>{t.transport_type}</span>
            </div>
            <span style={{ fontSize: 12, padding: "2px 7px", borderRadius: 20, background: t.is_active ? "#d8fffa" : "#F5F5F5", color: t.is_active ? "#208a3e" : "#B8B8B8", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
              {t.is_active ? "Active" : "Inactive"}
            </span>
            <button onClick={() => startEdit(t)} style={{ padding: "5px 12px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 11, cursor: "pointer" }}>Edit</button>
            <button onClick={() => handleToggleActive(t)} style={{ padding: "5px 12px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 11, cursor: "pointer" }}>{t.is_active ? "Deactivate" : "Activate"}</button>
            <button onClick={() => handleDelete(t)} style={{ padding: "5px 12px", background: "transparent", border: "1px solid #fca5a5", borderRadius: 7, color: "#e53935", fontSize: 11, cursor: "pointer" }}>Delete</button>
          </div>
        ))}
      </div>

      <Toast {...toast} />
    </div>
  );
}
